import { TonightMap, GearRecipe, PassPlot, SkyPlotView } from './DeferredViews.jsx';
import { useState } from 'react';
import TonightBriefing, { VerdictBlock } from './TonightBriefing.jsx';
import TonightDetails from './TonightDetails.jsx';
import MobilePassChips from './MobilePassChips.jsx';
import TonightStrip from './TonightStrip.jsx';
import VerdictDrilldown from './VerdictDrilldown.jsx';

// Activity id -> center render mode, mirroring App.jsx tonightMode so mobile and
// desktop pick the same chart/recipe for a given activity.
const TONIGHT_MODE = { iss: 'sat', aurora: 'sat', lunar: 'planet', deepsky: 'deepsky', sat: 'sat', planet: 'planet' };

// SKY / MAP / GEAR center toggle, mobile analog of the desktop center-view
// switch. SKY shows the pass plot or altitude chart, MAP the light-pollution
// map, GEAR the full gear guide.
const CENTER_TABS = [['sky', '✦ SKY'], ['map', '\u{1F5FA} MAP'], ['gear', '⚙ GEAR']];
const centerBtn = (on) => ({
  flex: 1, padding: '7px 0', borderRadius: 4, cursor: 'pointer',
  fontFamily: MONO, fontSize: 'var(--ui-label)', fontWeight: 700, letterSpacing: '0.08em',
  background: on ? 'var(--amber-2)' : 'var(--bg-2)',
  border: `1px solid ${on ? 'var(--accent)' : 'var(--hair-2)'}`,
  color: on ? 'var(--accent)' : 'var(--txt-mute)',
});

// Tonight pane, the mobile presentation of the desktop "Tonight" dim mode.
// There is no `dim` mode on mobile (the mobile shell is a separate render tree
// of tabs + bottom sheet), so rather than re-skin a 3-column layout we compose
// the same data-driven pieces into one vertical, sheet-scrollable document:
//
//   1. location header (GPS / change), preserves what the old Observer pane did
//   2. VerdictBlock, ring + activity selector + factor chips (the peek hero)
//   3. PassPlot, all-sky polar plot of the focused pass arc (rise/peak/set,
//      and the "enters shadow" fade when the pass crosses into Earth's umbra)
//   4. TonightBriefing (mobile, hideVerdict), mode tabs + target list
//   5. TonightDetails (mobile), Bortle / SQM / conditions / darker skies nearby
//
// The verdict fetch + tonightActivity state are lifted in App.jsx (shared with
// the desktop Tonight columns) and passed straight through.

const MONO = 'var(--mono)';

export default function MobilePaneTonight({
  observer, onEditLocation, pass, passes, focusedKey, onFocus,
  onLiveTrack, passLoading, passError, onSolar, verdict, loading, activity, onActivity, onPickSite, onGoto,
  // When the TONIGHT dim mode owns the center canvas (App.jsx), the SKY/MAP/
  // GEAR state is lifted there and this pane skips its embedded center block.
  centerView = null, onCenterView = null, hideCenter = false,
}) {
  const [localCenter, setLocalCenter] = useState('sky');
  const center = centerView ?? localCenter;
  const setCenter = onCenterView ?? setLocalCenter;
  const [drillFactor, setDrillFactor] = useState(null);
  const [drillOpen, setDrillOpen] = useState(false);
  const mode = TONIGHT_MODE[activity] || 'deepsky';

  if (!observer) {
    return <div className="og-location-prompt"><p>Personalize Tonight for your observing location.</p><button className="og-primary" onClick={onEditLocation}>Set location</button></div>;
  }

  return (
    <div className="og-tonight-brief">
      <button className="og-pane-location" onClick={onEditLocation}>{observer.name || "Coordinates"} · {observer.lat.toFixed(3)}°, {observer.lon.toFixed(3)}° · Change</button>

      <VerdictBlock verdict={verdict} loading={loading} activity={activity} onActivity={onActivity}
                    observer={observer} compact onFactorTap={(f) => { setDrillFactor(f); setDrillOpen(true); }} />

      {/* SKY / MAP / GEAR center toggle */}
      {!hideCenter && <div style={{ display: 'flex', gap: 4, margin: '8px 0' }} role="tablist" aria-label="Tonight view">
        {CENTER_TABS.map(([v, l]) => (
          <button key={v} role="tab" aria-selected={center === v}
                  onClick={() => setCenter(v)} style={centerBtn(center === v)}>
            {l}
          </button>
        ))}
      </div>}

      {!hideCenter && activity !== 'aurora' && center === 'sky' && mode === 'sat' && <MobilePassChips passes={passes} focusedKey={focusedKey} onFocus={onFocus}  />}
      {!hideCenter && center === 'sky' && (
        <>
          <div style={{
            position: 'relative', margin: '0 -12px',
            borderBottom: '1px solid var(--hair)',
            // PassPlot/SkyPlotView fill their parent (position:absolute inset:0),
            // so this wrapper MUST have an explicit height or it collapses to 0
            // and the plot renders on top of the content below.
            height: 380,
          }}>
            {mode === 'sat'
              ? <PassPlot pass={pass} observer={observer} onLiveTrack={onLiveTrack}
                  ghostPasses={(passes || []).filter(p => pass ? `${p.norad_id}:${p.start}` !== `${pass.norad_id}:${pass.start}` : true).slice(0, 2)} />
              : <SkyPlotView observer={observer} activity={mode}  />}
          </div>
        </>
      )}

      {/* Tonight timeline, same TonightStrip as the desktop bottom strip
          (pinch/zoom buttons included), in a fixed-height full-bleed band. */}
      {!hideCenter && activity !== 'aurora' && center === 'sky' && mode === 'sat' && (
        <div style={{
          position: 'relative', height: 100, margin: '0 -12px',
          borderBottom: '1px solid var(--hair)',
        }}>
          <TonightStrip observer={observer} verdict={verdict}
                        focusedKey={focusedKey} onFocus={onFocus} />
        </div>
      )}

      {!hideCenter && center === 'map' && (
        <div style={{
          position: 'relative', height: 420, margin: '0 -12px',
          borderTop: '1px solid var(--hair)', borderBottom: '1px solid var(--hair)',
        }}>
          <TonightMap mobile observer={observer} onPickSite={onPickSite} />
        </div>
      )}

      {!hideCenter && center === 'gear' && (
        <GearRecipe mobile observer={observer} verdict={verdict} activity={mode} items={[]} />
      )}

      {activity==='aurora' ? <section className="og-aurora-intro"><h3>Aurora outlook</h3><p>Use the local assessment above with current solar conditions. A satellite pass chart does not describe aurora visibility.</p><button onClick={onSolar}>Open Solar outlook</button></section> : <TonightBriefing
        mobile hideVerdict targetMode={mode} passes={passes} passLoading={passLoading} passError={passError} onFocus={onFocus} focusedKey={focusedKey}
        observer={observer} verdict={verdict} loading={loading}

        activity={activity} onActivity={onActivity} onGoto={onGoto}
      />}



      {drillOpen && (
        <VerdictDrilldown verdict={verdict} initialFactor={drillFactor}
                          onClose={() => { setDrillOpen(false); setDrillFactor(null); }} />
      )}
    </div>
  );
}
